import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SeoHelmet from '../components/SeoHelmet';
import Contact from '../components/Contact';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Services = () => {
  const { currentLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);
    loadData();
  }, [currentLanguage]);

  const loadData = async () => {
    try {
      const [catRes, servRes] = await Promise.all([
        axios.get(`${API_URL}/api/service-categories`),
        axios.get(`${API_URL}/api/services?language=${currentLanguage}`)
      ]);
      setCategories(catRes.data);
      setServices(servRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (key) => {
    const cat = categories.find(c => c.key === key);
    if (cat) {
      return currentLanguage === 'RU' ? cat.name_ru : cat.name_en;
    }
    return key;
  };

  const filteredServices = selectedCategory === 'all' 
    ? services 
    : services.filter(s => s.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SeoHelmet
        title={currentLanguage === 'EN'
          ? 'Our Services | VELOX HINT'
          : 'Наши услуги | ВЕЛОКС ХИНТ'}
        description={currentLanguage === 'EN'
          ? 'Full range of services related to non-tariff regulation and licensing.'
          : 'Полный спектр услуг по нератифному регулированию и лицензированию.'}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            {currentLanguage === 'RU' ? 'Наши услуги' : 'Our Services'}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {currentLanguage === 'RU' 
              ? 'Полный спектр услуг по нетарифному регулированию и лицензированию'
              : 'Full range of services related to non‑tariff regulation and licensing.'}
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Category Filter - loaded from API */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-6 py-3 rounded-full font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100 shadow'
              }`}
            >
              {currentLanguage === 'RU' ? 'Все услуги' : 'All Services'}
            </button>
            {categories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setSelectedCategory(cat.key)}
                className={`px-6 py-3 rounded-full font-medium transition-all ${
                  selectedCategory === cat.key
                    ? 'bg-orange-500 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow'
                }`}
              >
                {currentLanguage === 'RU' ? cat.name_ru : cat.name_en}
              </button>
            ))}
          </div>

          {/* Results Count */}
          <div className="text-center mb-8">
            <p className="text-gray-600">
              {currentLanguage === 'RU' 
                ? `Найдено услуг: ${filteredServices.length}`
                : `Services found: ${filteredServices.length}`}
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service) => (
              <div
                key={service.id}
                className="bg-white rounded-lg shadow-sm hover:shadow-lg transition overflow-hidden cursor-pointer group"
                onClick={() => navigate(`/services/${service.slug}`)}
              >
                {/* Preview Image */}
                
                
                <div className="p-6">
                  {/* Category Badge */}
                  <div className="mb-4">
                    <span className="inline-block px-3 py-1 bg-orange-100 text-orange-700 text-sm font-medium rounded-full">
                      {getCategoryName(service.category)}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-500 transition">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {service.description}
                  </p>

                  {/* Timeframe & Price */}
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{service.timeframe}</span>
                    </div>
                    {service.price && (
                      <span className="font-medium text-green-600">{service.price}</span>
                    )}
                  </div>

                  {/* Learn More Link */}
                  <div className="flex items-center text-orange-500 font-medium group-hover:text-orange-600">
                    <span>{currentLanguage === 'RU' ? 'Подробнее' : 'Learn more'}</span>
                    <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty state */}
          {filteredServices.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                {currentLanguage === 'RU' ? 'Услуги не найдены' : 'No services found'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <Contact />

      <Footer />
    </div>
  );
};

export default Services;
